/**
 * NipForm — Formularz NIP podmiotu z walidacją 10 cyfr + zapis przez API.
 *
 * Wyodrębnione z: /app/frontend/src/pages/settings/KsefSettingsPage.jsx
 * (linie 137-282 — handleSaveNip, validateNip, isNipChanged, canSaveNip, fetchSavedNip
 *  oraz JSX formularza NIP linie 991-1057)
 *
 * Props:
 *   - initialNip (string)   — NIP zapisany w backendzie (do porównania)
 *   - onSaved (callback)    — wywoływane po pomyślnym zapisie (otrzymuje nowy NIP)
 *
 * Komponent jest standalone — używa własnego stanu i własnych wywołań API.
 */
import { useState, useEffect } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Save, CheckCircle, AlertCircle, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { saveNip } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

// Walidacja NIP - 10 cyfr (logika 1:1 z KsefSettingsPage.jsx:222-227)
const validateNip = (nip) => {
  const nipClean = (nip || "").replace(/[-\s]/g, "");
  if (!nipClean) return "NIP jest wymagany";
  if (!/^\d{10}$/.test(nipClean)) return "NIP musi mieć dokładnie 10 cyfr";
  return null;
};

export const NipForm = ({ initialNip = "", onSaved }) => {
  const [nip, setNip] = useState(initialNip);
  const [savedNip, setSavedNip] = useState(initialNip);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Sync gdy initialNip przyjdzie z opóźnieniem (np. po fetchu w parent)
  useEffect(() => {
    if (initialNip && initialNip !== savedNip) {
      setNip(initialNip);
      setSavedNip(initialNip);
    }
  }, [initialNip]); // eslint-disable-line react-hooks/exhaustive-deps

  const isNipChanged = () => {
    const currentClean = (nip || "").replace(/[-\s]/g, "");
    const savedClean = (savedNip || "").replace(/[-\s]/g, "");
    return currentClean !== savedClean;
  };

  const canSave = () => {
    const err = validateNip(nip);
    return !err && isNipChanged();
  };

  const handleSave = async () => {
    setError(null);

    const validationError = validateNip(nip);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    if (!isNipChanged()) {
      toast.info("NIP nie został zmieniony");
      return;
    }

    setSaving(true);
    try {
      const response = await saveNip(nip);
      if (response.data.ok) {
        toast.success("Zapisano konfigurację KSeF");
        const cleanNip = nip.replace(/[-\s]/g, "");
        setSavedNip(cleanNip);
        if (onSaved) onSaved(cleanNip);
      } else {
        toast.error(response.data.message || "Błąd zapisywania NIP");
      }
    } catch (err) {
      logger.error("Error saving NIP:", err);
      const errorMsg = safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd zapisywania NIP";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const changed = isNipChanged();

  return (
    <div className="space-y-3" data-testid="nip-form">
      <Label htmlFor="company_nip" className="text-sm font-medium text-gray-700">
        NIP podmiotu (kontekst KSeF)
      </Label>
      <div className="flex gap-3">
        <div className="flex-1">
          <Input
            id="company_nip"
            placeholder="0000000000"
            value={nip}
            onChange={(e) => {
              setError(null);
              setNip(e.target.value.replace(/[^0-9-]/g, ""));
            }}
            className={`font-mono ${error ? "border-red-500" : changed ? "border-yellow-500" : ""}`}
            maxLength={13}
            data-testid="nip-form-input"
          />
        </div>
        <Button
          onClick={handleSave}
          disabled={!canSave() || saving}
          className={`min-w-[120px] ${canSave() ? "bg-green-600 hover:bg-green-700" : "bg-gray-400"}`}
          data-testid="nip-form-save-btn"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Zapisuję...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Zapisz NIP
            </>
          )}
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            {error}
          </p>
        )}
        {changed && !error && (
          <p className="text-xs text-yellow-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            NIP został zmieniony. Kliknij "Zapisz NIP" aby zapisać.
          </p>
        )}
        {savedNip && !changed && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            NIP zapisany: {savedNip.slice(0, 3)}***{savedNip.slice(-4)}
          </p>
        )}
        {!savedNip && !nip && (
          <p className="text-xs text-gray-500">
            NIP firmy, dla której pobierasz faktury z KSeF
          </p>
        )}
      </div>
    </div>
  );
};

export default NipForm;
